import ApartmentOutlined from '@ant-design/icons/ApartmentOutlined';
import BookOutlined from '@ant-design/icons/BookOutlined';
import DashboardOutlined from '@ant-design/icons/DashboardOutlined';
import FileImageOutlined from '@ant-design/icons/FileImageOutlined';
import GiftOutlined from '@ant-design/icons/GiftOutlined';
import LogoutOutlined from '@ant-design/icons/LogoutOutlined';
import ProductOutlined from '@ant-design/icons/ProductOutlined';
import SecurityScanOutlined from '@ant-design/icons/SecurityScanOutlined';
import ShoppingCartOutlined from '@ant-design/icons/ShoppingCartOutlined';
import TagsOutlined from '@ant-design/icons/TagsOutlined';
import UserOutlined from '@ant-design/icons/UserOutlined';
import Button from 'antd/lib/button';
import Menu from 'antd/lib/menu';
import Badge from 'antd/lib/badge';
import PropTypes from 'prop-types';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { getConfirmedOrderAndUnApprovedUserCount } from '../api/analyticsApi';
import { ROUTES } from '../constant/constants';
import { AuthContext } from '../context/AuthProvider';

function getItem(label, key, icon, children) {
  return {
    key,
    icon,
    children,
    label,
  };
}

const CommonLayout = ({ children }) => {
  const [isHovered, setIsHovered] = useState(false);
  const [orderCount, setOrderCount] = useState(0);
  const [unApprovedUserCount, setUnApprovedUserCount] = useState(0);
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated, logout } = useContext(AuthContext);

  const handleMenuClick = useCallback(
    ({ key }) => {
      navigate(key);
    },
    [navigate],
  );

  const handleSignOutClick = useCallback(() => {
    logout();
  }, [logout]);

  const handleMouseEnter = useCallback(() => {
    setIsHovered(true);
  }, []);

  const handleMouseLeave = useCallback(() => {
    setTimeout(() => {
      setIsHovered(false);
    }, 100);
  }, []);

  // Notify components (e.g., tables) to recalculate layout when sidebar width changes
  useEffect(() => {
    const timeout = setTimeout(() => {
      try {
        window.dispatchEvent(new Event('resize'));
      } catch (e) {}
    }, 350); // allow transition to complete
    return () => clearTimeout(timeout);
  }, [isHovered]);

  // Fetch confirmed order count and unapproved user count
  const fetchCounts = useCallback(async () => {
    try {
      const response = await getConfirmedOrderAndUnApprovedUserCount();
      if (response.code === 200) {
        setOrderCount(response.data?.confirmedOrderCount || 0);
        setUnApprovedUserCount(response.data?.unApproveUserCount || 0);
      }
    } catch (error) {
      console.error('Error fetching counts:', error);
    }
  }, []);

  // Fetch counts on component mount and when authenticated
  useEffect(() => {
    if (isAuthenticated) {
      fetchCounts();
      // Refresh counts every 30 seconds
      const interval = setInterval(fetchCounts, 30000);
      return () => clearInterval(interval);
    }
  }, [isAuthenticated, fetchCounts]);

  const menuItems = [
    getItem('Dashboard', ROUTES.HOME, <DashboardOutlined />),
    getItem(
      'User Management',
      ROUTES.USERS,
      <Badge
        count={unApprovedUserCount > 0 ? unApprovedUserCount : 0}
        size='small'
        offset={[6, -2]}
        style={{ backgroundColor: 'var(--color-info)' }}
      >
        <span className='inline-flex w-5 h-5 items-center justify-center'>
          <UserOutlined />
        </span>
      </Badge>,
    ),
    getItem(
      'Order Management',
      ROUTES.ORDER,
      <Badge
        count={orderCount > 0 ? orderCount : 0}
        size='small'
        offset={[6, -2]}
        style={{ backgroundColor: 'var(--color-accent)' }}
      >
        <span className='inline-flex w-5 h-5 items-center justify-center'>
          <ShoppingCartOutlined />
        </span>
      </Badge>,
    ),
    getItem('Category Management', ROUTES.PRODUCT_CATEGORY, <TagsOutlined />),
    getItem('Product Management', ROUTES.PRODUCT, <ProductOutlined />),
    getItem('Spare Part Management', ROUTES.SPARE_PART, <ApartmentOutlined />),
    getItem('Catalogue', ROUTES.CATALOGUE, <BookOutlined />),
    getItem('Banner Image', ROUTES.BANNER_IMAGE, <FileImageOutlined />),
    getItem('Promotional Banner', ROUTES.PROMOTIONAL_BANNER, <GiftOutlined />),
    getItem('Privacy Policy', ROUTES.PRIVACY_POLICY, <SecurityScanOutlined />),
    getItem(
      'Terms And Condition',
      ROUTES.TERMS_AND_CONDITION,
      <BookOutlined />,
    ),
  ];

  const routeTitleMap = {
    [ROUTES.HOME]: 'Dashboard',
    [ROUTES.USERS]: 'User Management',
    [ROUTES.ORDER]: 'Order Management',
    [ROUTES.PRODUCT_CATEGORY]: 'Category Management',
    [ROUTES.PRODUCT]: 'Product Management',
    [ROUTES.SPARE_PART]: 'Spare Part Management',
    [ROUTES.CATALOGUE]: 'Catalogue',
    [ROUTES.BANNER_IMAGE]: 'Banner Image',
    [ROUTES.PROMOTIONAL_BANNER]: 'Promotional Banner',
    [ROUTES.PRIVACY_POLICY]: 'Privacy Policy',
    [ROUTES.TERMS_AND_CONDITION]: 'Terms And Condition',
  };

  const routeIconMap = {
    [ROUTES.HOME]: <DashboardOutlined className='text-white text-lg' />,
    [ROUTES.USERS]: <UserOutlined className='text-white text-lg' />,
    [ROUTES.ORDER]: <ShoppingCartOutlined className='text-white text-lg' />,
    [ROUTES.PRODUCT_CATEGORY]: <TagsOutlined className='text-white text-lg' />,
    [ROUTES.PRODUCT]: <ProductOutlined className='text-white text-lg' />,
    [ROUTES.SPARE_PART]: <ApartmentOutlined className='text-white text-lg' />,
    [ROUTES.CATALOGUE]: <BookOutlined className='text-white text-lg' />,
    [ROUTES.BANNER_IMAGE]: <FileImageOutlined className='text-white text-lg' />,
    [ROUTES.PROMOTIONAL_BANNER]: (
      <GiftOutlined className='text-white text-lg' />
    ),
    [ROUTES.PRIVACY_POLICY]: (
      <SecurityScanOutlined className='text-white text-lg' />
    ),
    [ROUTES.TERMS_AND_CONDITION]: (
      <BookOutlined className='text-white text-lg' />
    ),
  };

  const isExpanded = isHovered;

  return (
    <div className='flex h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50'>
      {/* Sidebar */}
      <div
        className={`bg-white/80 backdrop-blur shadow-xl rounded-r-2xl transition-all duration-300 ease-in-out flex flex-col relative z-10 ${
          isExpanded ? 'w-64' : 'w-24'
        }`}
        style={{
          width: isExpanded ? '256px' : '80px',
          minWidth: isExpanded ? '256px' : '80px',
          maxWidth: isExpanded ? '256px' : '80px',
        }}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        role='navigation'
        aria-label='Main navigation'
      >
        {/* Logo Section */}
        <div className='flex items-center justify-center p-4 border-b border-border/60 min-h-[80px]'>
          {isExpanded ? (
            <div className='w-36 h-16 rounded-xl bg-gradient-to-r from-blue-600 to-purple-600 flex items-center justify-center shadow'>
              <img
                src='/png/megasun-logo.png'
                className='max-w-[120px] max-h-[40px] object-contain'
                alt='Megasun Logo'
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'flex';
                }}
              />
              <div className='hidden items-center justify-center w-full h-full'>
                <span className='text-white font-bold text-lg'>MEGASUN</span>
              </div>
            </div>
          ) : null}
          {isExpanded ? (
            <div className='hidden items-center justify-center w-32 h-14 bg-gradient-to-r from-blue-500 to-purple-600 rounded-lg shadow'>
              <span className='text-white font-bold text-lg'>MEGASUN</span>
            </div>
          ) : (
            <div className='flex items-center justify-center w-8 h-8 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full shadow-sm'>
              <span className='text-white font-bold text-sm'>M</span>
            </div>
          )}
        </div>

        {/* Navigation Menu */}
        <div className='flex-1 overflow-y-auto'>
          <Menu
            mode='vertical'
            selectedKeys={[location.pathname]}
            items={menuItems}
            onClick={handleMenuClick}
            className='border-none rounded-xl'
            inlineCollapsed={!isExpanded}
            style={{ height: '100%', overflow: 'visible' }}
          />
        </div>

        {/* Sign Out Button */}
        {isAuthenticated && (
          <div className='p-4 border-t border-border/60'>
            <Button
              type='primary'
              danger
              icon={<LogoutOutlined />}
              onClick={handleSignOutClick}
              className='w-full flex items-center justify-center shadow'
              size='large'
            >
              {isExpanded && 'Sign Out'}
            </Button>
          </div>
        )}
      </div>

      {/* Main Content */}
      <div className='flex-1 overflow-hidden flex flex-col'>
        {isAuthenticated && (
          <div className='bg-white/80 backdrop-blur border-b border-border/60 px-6 py-4 flex justify-between items-center sticky top-0 z-0 shadow-sm'>
            <div className='flex items-center gap-3'>
              <div className='p-2 rounded-lg shadow bg-gradient-to-r from-blue-500 to-purple-600'>
                {routeIconMap[location.pathname]}
              </div>
              <div>
                <div className='text-xl font-bold text-text'>
                  {routeTitleMap[location.pathname] || ''}
                </div>
                <div className='text-muted text-sm'>Admin Portal</div>
              </div>
            </div>
            <div className='flex items-center gap-3'></div>
          </div>
        )}

        {/* Content Area */}
        <div className='flex-1 overflow-y-auto p-6'>{children}</div>
      </div>
    </div>
  );
};

CommonLayout.propTypes = {
  children: PropTypes.node.isRequired,
};

export default CommonLayout;
